import React, { useMemo } from "react";
import { Invoice } from "@common/types/invoice";
import { usePatchImporterInvoices } from "@api/invoice.api";
import { AccrualImporterDrawer } from "@components/accrualImporter/AccrualImporterDrawer";
import { mapInvoicesToImporterRows } from "@components/accrualImporter/mapAccrualsToImporterRows";
import { useAccrualEditSubmit } from "@components/accrualImporter/useAccrualEditSubmit";
import { useInvoiceEditScheduleFeedback } from "./useInvoiceEditScheduleFeedback";

interface Props {
    invoices: Invoice[];
    open?: boolean;
    onClose: () => void;
}

export const ManualInvoiceEditDrawer: React.FC<Props> = ({
    invoices,
    open,
    onClose,
}) => {
    const { notifyScheduleChanges, handleSaveError } =
        useInvoiceEditScheduleFeedback();
    const patchInvoices = usePatchImporterInvoices({
        onSuccess: (response) =>
            notifyScheduleChanges(response.scheduleChanges),
    });
    const initialRows = useMemo(
        () => mapInvoicesToImporterRows(invoices),
        [invoices],
    );
    const originalAccrualIds = useMemo(
        () => invoices.map((inv) => inv.id),
        [invoices],
    );

    const handleSubmit = useAccrualEditSubmit({
        mode: "invoice",
        originalAccrualIds,
        mutateAsync: patchInvoices.mutateAsync as (
            params: Record<string, unknown>,
        ) => Promise<unknown>,
        onClose,
        successMessage: "Invoices updated",
        errorMessage: "Failed to update invoices",
        onError: handleSaveError,
    });

    return (
        <AccrualImporterDrawer
            mode="invoice"
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            isSubmitting={patchInvoices.isPending}
            initialRows={initialRows}
            title="Edit invoices"
        />
    );
};
